/**
 * Unified Authentication Functions
 * 
 * Provider-agnostic authentication utilities that work with both Okta and MSAL
 */

import { AUTH_PROVIDER, isOkta, isMsal } from './auth-config';

// Lazy imports to avoid loading unused provider code
let oktaAuth = null;
let msalAuth = null;

// Initialize Okta if needed
const getOktaAuth = async () => {
  if (!oktaAuth) {
    const { getOktaAuth: initOkta } = await import('../okta/okta-auth');
    oktaAuth = initOkta();
  }
  return oktaAuth;
};

// Initialize MSAL if needed
const getMsalInstance = async () => {
  if (!msalAuth) {
    const { default: msalInstance } = await import('../msal/msal-config');
    // Ensure MSAL is initialized before using it
    await msalInstance.initialize();
    msalAuth = msalInstance;
  }
  return msalAuth;
};

/**
 * Login - Redirect user to authentication provider
 */
export const login = async () => {
  try {
    if (isOkta()) {
      const { oktaLogin } = await import('../okta/okta-auth');
      const auth = await getOktaAuth();
      return await oktaLogin(auth);
    } else if (isMsal()) {
      const { msalLogin } = await import('../msal/msal-auth');
      return await msalLogin();
    }
  } catch (error) {
    console.error('Login failed:', error);
    throw error;
  }
};

/**
 * Logout - Sign out user and clear session
 */
export const logout = async () => {
  try {
    // Clear common localStorage items
    localStorage.removeItem("ph-login-time");
    localStorage.removeItem("ph-login-time-display");
    localStorage.removeItem("ph-alert-shown");
    localStorage.removeItem("access_token");
    localStorage.removeItem("userData");
    
    if (isOkta()) {
      const auth = await getOktaAuth();
      return auth.signOut();
    } else if (isMsal()) {
      const { msalLogout } = await import('../msal/msal-auth');
      return await msalLogout();
    }
  } catch (error) {
    console.error('Logout failed:', error);
    throw error;
  }
};

/**
 * Get Authentication Header for API calls
 * Returns { Authorization: 'Bearer <token>' }
 */
export const getAuthHeader = async () => {
  try {
    if (isOkta()) {
      const { getOktaAuthHeader } = await import('../okta/okta-auth');
      return getOktaAuthHeader();
    } else if (isMsal()) {
      const { getMsalAuthHeader } = await import('../msal/msal-auth');
      return await getMsalAuthHeader();
    }
    return {};
  } catch (error) {
    console.error('Failed to get auth header:', error);
    return {};
  }
};

/**
 * Get User Claims/Profile Information
 * Returns standardized user object regardless of provider
 */
export const getUserClaims = async () => {
  try {
    if (isOkta()) {
      const { getOktaUserClaims } = await import('../okta/okta-auth');
      const auth = await getOktaAuth();
      return await getOktaUserClaims(auth);
    } else if (isMsal()) {
      const { getMsalUserClaims } = await import('../msal/msal-auth');
      return getMsalUserClaims();
    }
    return null;
  } catch (error) {
    console.error('Failed to get user claims:', error);
    return null;
  }
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = async () => {
  try {
    if (isOkta()) {
      // For Okta, check from authState (will be passed from hook)
      return false; // This will be handled by useAuth hook
    } else if (isMsal()) {
      const { isMsalAuthenticated } = await import('../msal/msal-auth');
      return isMsalAuthenticated();
    }
    return false;
  } catch (error) {
    console.error('Failed to check authentication:', error);
    return false;
  }
};

/**
 * Renew/Refresh authentication tokens
 */
export const renewToken = async () => {
  try {
    if (isOkta()) {
      const { getRenewRefreshToken } = await import('../okta/okta-auth');
      const auth = await getOktaAuth();
      return await getRenewRefreshToken(auth);
    } else if (isMsal()) {
      const { getRenewRefreshToken } = await import('../msal/msal-auth');
      return await getRenewRefreshToken();
    }
    return null;
  } catch (error) {
    console.error('Token renewal failed:', error);
    throw error;
  }
};

/**
 * Get the auth instance (for provider-specific operations)
 */
export const getAuthInstance = async () => {
  if (isOkta()) {
    return await getOktaAuth();
  } else if (isMsal()) {
    return await getMsalInstance();
  }
  return null;
};

/**
 * Set original URI for post-login redirect
 */
export const setOriginalUri = async (uri) => {
  try {
    if (isOkta()) {
      const auth = await getOktaAuth();
      auth.setOriginalUri(uri);
    } else if (isMsal()) {
      // MSAL handles this automatically
      sessionStorage.setItem('msal.original.uri', uri);
    }
  } catch (error) {
    console.error('Failed to set original URI:', error);
  }
};

export default {
  login,
  logout,
  getAuthHeader,
  getUserClaims,
  isAuthenticated,
  renewToken,
  getAuthInstance,
  setOriginalUri,
  AUTH_PROVIDER,
};
